import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toDate, toDateInputValue, toLocalDateTimeValue } from "./formatDate";
import { DateInputProps } from "./DateInput.types";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Fixed range (not relative to the displayed month) so the list doesn't
// shift as the user navigates the calendar.
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from(
  { length: CURRENT_YEAR + 10 - (CURRENT_YEAR - 100) + 1 },
  (_, index) => CURRENT_YEAR - 100 + index
).reverse();

// Auto-inserts the "/" separators as the user types digits (dd/MM/yyyy),
// so they don't have to type them manually.
function insertDateSeparators(rawValue: string): string {
  const digits = rawValue.replace(/\D/g, "").slice(0, 8);

  if (digits.length > 4) {
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  }
  if (digits.length > 2) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }
  return digits;
}

// For a date alone: with a time after it, the separators would cut the time off.
function addSeparatorsAsTyped(event?: { target: EventTarget | null }) {
  const input = event?.target as HTMLInputElement | undefined;
  if (typeof input?.value !== "string") {
    return;
  }
  input.value = insertDateSeparators(input.value);
}

/**
 * The date picker on its own, without a field's label and messages - `DateField` wraps
 * it for a page, and the designer's panels place it under labels of their own. Uses
 * `react-datepicker` rather than the native `<input type="date">`, whose segments reset
 * each other mid-edit. Hands back yyyy-MM-dd, or "" when cleared. Clearable whenever it
 * is editable, required or not: an emptied required date is the save's check to report.
 */
export function DateInput({
  id,
  name,
  value,
  onChange,
  disabled,
  required,
  max,
  min,
  className = "form-control",
  placeholder,
  ariaDescribedBy,
  ariaInvalid,
  showTime = false,
}: DateInputProps) {
  return (
    <DatePicker
      id={id}
      // react-datepicker owns the input and takes these as props of its own.
      ariaDescribedBy={ariaDescribedBy}
      ariaInvalid={ariaInvalid ? "true" : undefined}
      name={name ?? id}
      selected={toDate(value)}
      onChange={(date) =>
        onChange(
          date ? (showTime ? toLocalDateTimeValue(date) : toDateInputValue(date)) : ""
        )
      }
      dateFormat={showTime ? "dd/MM/yyyy HH:mm" : "dd/MM/yyyy"}
      showTimeSelect={showTime}
      timeFormat="HH:mm"
      timeIntervals={15}
      timeCaption="Time"
      placeholderText={placeholder ?? (showTime ? "dd/mm/yyyy hh:mm" : "dd/mm/yyyy")}
      className={className}
      // react-datepicker's wrapper is `display: inline-block` with no width, so it
      // shrink-wraps to the input's own ~190px whatever column it is in. Wide enough
      // and the label - an inline <label> - fits beside it and stops sitting above the
      // field; narrow enough and the input overruns the column instead.
      wrapperClassName="d-block"
      disabled={disabled}
      required={required}
      maxDate={max ? (toDate(max) ?? undefined) : undefined}
      minDate={min ? (toDate(min) ?? undefined) : undefined}
      isClearable={!disabled}
      autoComplete="off"
      onChangeRaw={showTime ? undefined : addSeparatorsAsTyped}
      // `form-select` month/year dropdowns, replacing the plaintext "Month Year" header.
      renderCustomHeader={({ date, changeMonth, changeYear }) => (
        <div className="d-flex gap-2 px-2 pb-2">
          <select
            className="form-select"
            value={date.getMonth()}
            onChange={(event) => changeMonth(Number(event.target.value))}
          >
            {MONTH_NAMES.map((month, index) => (
              <option key={month} value={index}>
                {month}
              </option>
            ))}
          </select>
          <select
            className="form-select"
            value={date.getFullYear()}
            onChange={(event) => changeYear(Number(event.target.value))}
          >
            {YEARS.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      )}
    />
  );
}

export default DateInput;
